// The end of a Learn session (mockup l03, provisional): the goal ring, and when today's goal was
// just reached, "Goal done!" with the play it earned (paid by useDailyGoalReward). WI-11: a
// summary every time: the right answers that counted toward the goal and the XP, each card once
// (✓ or ✗), the ones met for the first time and the ones to try again. Never "5 of 7 right": a
// re-asked card isn't another card.
import { motion } from 'motion/react'
import { useEffect, useId, useMemo, useRef } from 'react'
import type { Pack } from '../../shared/pack/schema'
import { closeOverlay, goTab } from '../app/router'
import { useModal } from '../app/Sheet'
import { playsChime } from '../audio/synth'
import { contentLang } from '../audio/tts'
import { rightOn } from '../domain/arcade/plays'
import { streak } from '../domain/arcade/progress'
import { sessionSummary } from '../domain/learn'
import { addDays } from '../domain/time'
import { springs } from '../motion/springs'
import { useMotionTier } from '../motion/tier'
import { GoalRing } from '../screens/LearnScreen'
import { t } from '../strings/en'
import { useActive, useArcade } from './useArcade'

export function LearnDone({ pack, personId }: { pack: Pack; personId: string }) {
  const done = useActive((s) => (s.done?.personId === personId ? s.done : null))
  const { events, settings, rules, progress, restDays } = useArcade(pack, personId)
  const tier = useMotionTier()
  const still = tier === 'still'
  const goal = settings?.learn.goal
  const day = done?.day ?? ''
  const answered = rightOn(events, personId, day)
  // Reached in this session: before its right answers, the count was under the goal.
  const justMet = goal !== undefined && answered >= goal && answered - (done?.right ?? 0) < goal
  const summary = useMemo(() => (done ? sessionSummary(done.items, done.results) : null), [done])

  useEffect(() => {
    if (!done) closeOverlay()
    else if (justMet && tier === 'full' && rules.daily.on) playsChime()
  }, [done, justMet, tier, rules.daily.on])

  const ref = useRef<HTMLDivElement>(null)
  const headId = useId()
  useModal(ref, { active: !!done })

  if (!done || !summary) return null
  const list = (cards: { label: string }[]) => cards.map((c) => c.label).join(', ')
  return (
    <div ref={ref} className={`reward learn-done${justMet ? ' met' : ''}`} role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby={headId}>
      <div className={`rays${tier !== 'full' ? ' paused' : ''}`} aria-hidden />
      <motion.div className="learn-done-ring" initial={still ? false : { scale: 0.4 }} animate={{ scale: 1 }} transition={springs.slap}>
        {goal !== undefined ? <GoalRing done={answered} goal={goal} size={200} /> : null}
      </motion.div>
      <div className="reward-text">
        <h2 id={headId}>{justMet ? t.learn.goalMet : t.learn.sessionDone}</h2>
        {justMet && rules.daily.on ? <p>{t.learn.metSub(rules.daily.plays, streak(progress, day, addDays(day, -1), restDays))}</p> : null}
        <p className="learn-sum-line">
          {t.learn.summaryRight(done.right)}
          {done.xp > 0 ? <span className="tag yellow">{t.learn.summaryXp(done.xp)}</span> : null}
        </p>
        {summary.cards.length ? (
          <ul className="learn-sum-cards" aria-label={t.learn.summaryCards}>
            {summary.cards.map((c) => (
              <li key={c.cardId} className={c.ok ? 'ok' : 'miss'} aria-label={c.ok ? t.learn.summaryOk(c.label) : t.learn.summaryMiss(c.label)}>
                <b lang={contentLang()}>{c.label}</b>
                <i aria-hidden>{c.ok ? '✓' : '✗'}</i>
              </li>
            ))}
          </ul>
        ) : null}
        {summary.met.length ? (
          <small className="learn-sum-note">
            {t.learn.summaryMet(list(summary.met))}
          </small>
        ) : null}
        {summary.again.length ? (
          <small className="learn-sum-note">
            {t.learn.summaryAgain(list(summary.again))}
          </small>
        ) : null}
      </div>
      <div className="reward-foot">
        {justMet ? (
          <button className="btn y press" onClick={() => goTab('arcade')}>
            {t.learn.toArcade}
          </button>
        ) : null}
        <button className={`btn ${justMet ? 'w' : 'y'} press`} onClick={closeOverlay}>
          {t.learn.keepLearning}
        </button>
      </div>
    </div>
  )
}
